import { useState } from "react";
import {
  TransformWrapper,
  TransformComponent,
  useControls,
} from "react-zoom-pan-pinch";
import {
  MinusIcon as Minus,
  PlusIcon as Plus,
  ArrowsOutSimpleIcon as ArrowsOutSimple,
  CornersOutIcon as CornersOut,
} from "@phosphor-icons/react";

/** Inner button used by the toolbar. Module-scoped so React reuses the
 *  same component type across renders (avoids unmount-on-each-render). */
function ToolbarBtn({ onClick, testid, title, children, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      title={title}
      className="w-8 h-8 flex items-center justify-center border-r last:border-r-0 border-[var(--ml-border)] hover:bg-[var(--ml-surface)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      {children}
    </button>
  );
}

/** Toolbar that lives inside <TransformWrapper> so it can call
 *  zoomIn / zoomOut / resetTransform via the imperative hook. */
function ZoomToolbar({ scale, minScale, maxScale }) {
  const { zoomIn, zoomOut, resetTransform } = useControls();
  const percent = Math.round(scale * 100);
  return (
    <div
      className="absolute top-2 right-2 z-10 flex items-stretch border border-[var(--ml-border)] bg-white shadow-sm"
      data-testid="zoom-toolbar"
    >
      <ToolbarBtn
        onClick={() => zoomOut()}
        testid="zoom-out"
        title="Zoom out"
        disabled={scale <= minScale + 0.001}
      >
        <Minus size={13} weight="bold" />
      </ToolbarBtn>
      <span
        className="px-2 flex items-center ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] border-r border-[var(--ml-border)] tabular-nums select-none min-w-[3.5rem] justify-center"
        data-testid="zoom-level"
      >
        {percent}%
      </span>
      <ToolbarBtn
        onClick={() => zoomIn()}
        testid="zoom-in"
        title="Zoom in"
        disabled={scale >= maxScale - 0.001}
      >
        <Plus size={13} weight="bold" />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => resetTransform()}
        testid="zoom-fit"
        title="Fit to view (reset)"
      >
        <ArrowsOutSimple size={13} weight="bold" />
      </ToolbarBtn>
    </div>
  );
}

/** Hint pill at the bottom-left of the canvas — disappears after the first
 *  interaction so it never blocks the diagram for a returning user. */
function PanHint({ visible }) {
  if (!visible) return null;
  return (
    <div
      className="absolute bottom-2 left-2 z-10 px-2 py-1 bg-white/85 backdrop-blur-sm border border-[var(--ml-border)] ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5 pointer-events-none"
      data-testid="pan-hint"
    >
      <CornersOut size={11} weight="bold" />
      Drag to pan · scroll to zoom
    </div>
  );
}

/** Pannable / zoomable canvas wrapper. Used to keep large impact-graphs
 *  navigable when the underlying SVG would otherwise overflow the viewport. */
export default function ZoomablePane({ children, minScale = 0.25, maxScale = 3 }) {
  const [scale, setScale] = useState(1);
  const [showHint, setShowHint] = useState(true);

  return (
    <div
      className="relative ml-card bg-white"
      style={{ height: "min(70vh, 720px)" }}
      data-testid="zoomable-pane"
    >
      <TransformWrapper
        initialScale={1}
        minScale={minScale}
        maxScale={maxScale}
        limitToBounds={false}
        centerOnInit
        wheel={{ step: 0.15 }}
        doubleClick={{ disabled: true }}
        onTransform={(_ref, state) => setScale(state.scale)}
        onPanningStart={() => setShowHint(false)}
        onZoomStart={() => setShowHint(false)}
      >
        <ZoomToolbar scale={scale} minScale={minScale} maxScale={maxScale} />
        <PanHint visible={showHint} />
        <TransformComponent
          wrapperStyle={{
            width: "100%",
            height: "100%",
            cursor: "grab",
          }}
          contentStyle={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minWidth: "100%",
            minHeight: "100%",
            padding: "16px",
          }}
        >
          {children}
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
}
